import { createContext, useEffect, useState } from "react";

import { apiFetch } from "./utils/api-fetch";

import type {
  InactiveSession,
  Session,
  SetUserSession,
  UserSession,
} from "./session";

const sessionKey = "sp_session";

const isExpired = (value: string): boolean => new Date() > new Date(value);

const getSession = (): Session | undefined => {
  const sessionDataString =
    sessionStorage.getItem(sessionKey) || localStorage.getItem(sessionKey);

  return sessionDataString
    ? (JSON.parse(sessionDataString) as Session)
    : undefined;
};

const setSession = (session: Session, stayLoggedIn: boolean): void => {
  const storage = stayLoggedIn ? localStorage : sessionStorage;
  storage.setItem(sessionKey, JSON.stringify(session));
};

export const UserSessionContext = createContext<
  [Session | null, SetUserSession]
>([
  null,
  (): InactiveSession => ({
    active: false,
  }),
]);

export const UserSessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [userSession, setUserSessionInternal] = useState<Session | null>(null);

  const setUserSession = (session: Session, stayLoggedIn?: boolean): void => {
    setSession(session, stayLoggedIn ?? false);
    setUserSessionInternal(session);
  };

  useEffect(() => {
    const session = getSession();

    if (session && !session.active) {
      setUserSessionInternal({ active: false });
      return;
    }

    if (session && !isExpired(session.expirationDate)) {
      apiFetch(session.sessionToken)(
        `/api/login/session/${session.sessionId}`,
      ).then((response) => {
        if (response.ok) {
          response.json().then((userSession: UserSession) => {
            setUserSession({ active: true, ...userSession });
          });
        } else {
          setUserSessionInternal({ active: false });
        }
      });
    } else {
      setUserSessionInternal({ active: false });
    }
  }, []);

  return (
    <UserSessionContext.Provider value={[userSession, setUserSession]}>
      {children}
    </UserSessionContext.Provider>
  );
};
